import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import './Home.css'
import './Home-responsive.css';
import { Slide, Fade } from 'react-awesome-reveal';
import Tilt from 'react-parallax-tilt';
import { Ripple } from "primereact/ripple";
import { Dialog } from "primereact/dialog";
import { Tooltip as InfoTootip } from "primereact/tooltip";
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    LineElement,
    LinearScale,
    PointElement,
    Tooltip,
    Legend,
    Filler,
    TooltipItem,
    ChartOptions,
    CategoryScale,
} from 'chart.js';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperClass } from 'swiper/types';
import { Navigation, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';

import { goToTop } from "../../../WebApp/Components/goToTop";
import JoinUs from "../CommonSections/JoinUs";
import DownloadApp from "../CommonSections/DownloadApp";
import { Testimonials, testimonials } from "./HomeData";
import { playStoreLink, appStoreLink } from "../../Utils/Info";

interface FeatureTask {
    id: number;
    title: string;
}

ChartJS.register(LineElement, LinearScale, PointElement, Tooltip, Legend, Filler, CategoryScale);

const Home: React.FC = () => {
    const downloadAppRef = useRef<HTMLDivElement>(null);

    const handleScrollToDownloadApp = (e: React.MouseEvent) => {
        e.preventDefault();
        if (downloadAppRef.current) {
            const position = downloadAppRef.current.getBoundingClientRect().top + window.pageYOffset;
            window.scrollTo({
                top: position - 60,
                behavior: 'smooth',
            });
        }
    };

    /* *********************************** For feature task *********************************** */
    const [featureTasks, setFeatureTasks] = useState<FeatureTask[]>([
        { id: 1, title: 'Planning' },
        { id: 2, title: 'Sketch' },
        { id: 3, title: 'Quote' },
        { id: 4, title: 'Approval' },
    ]);

    const [draggedTaskId, setDraggedTaskId] = useState<number | null>(null);

    const handleDragStart = (taskId: number) => {
        setDraggedTaskId(taskId);
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
    };

    const handleDrop = (taskId: number) => {
        if (draggedTaskId !== null && draggedTaskId !== taskId) {
            reorderTasks(draggedTaskId, taskId);
        }
        setDraggedTaskId(null);
    };

    const handleClick = (taskId: number) => {
        reorderTasks(taskId);
    };

    const reorderTasks = (movedTaskId: number, targetTaskId?: number) => {
        const movedIndex = featureTasks.findIndex((task) => task.id === movedTaskId);
        const reorderedTasks = [...featureTasks];

        if (targetTaskId) {
            const targetIndex = featureTasks.findIndex((task) => task.id === targetTaskId);
            const [movedTask] = reorderedTasks.splice(movedIndex, 1);
            reorderedTasks.splice(targetIndex, 0, movedTask);
        } else {
            const [movedTask] = reorderedTasks.splice(movedIndex, 1);
            reorderedTasks.unshift(movedTask);
        }

        setFeatureTasks(reorderedTasks);
    };
    /* **************************************************************************************** */


    /* *********************************** For feature graph *********************************** */
    const chartData = {
        labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May'],
        datasets: [
            {
                label: 'SPENT',
                data: [0, 2.5, 2.5, 4, 2.5],
                borderColor: '#512888',
                backgroundColor: 'rgba(81, 40, 136, 0.3)',
                fill: true,
                tension: 0,
            },
            {
                label: 'REVENUE',
                data: [0, 7.5, 6, 17, 25],
                borderColor: '#F37C47',
                backgroundColor: 'rgba(243, 124, 71, 0.3)',
                fill: true,
                tension: 0,
            },
        ],
    };

    const options: ChartOptions<'line'> = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top' as const,
            },
            tooltip: {
                callbacks: {
                    label: (tooltipItem: TooltipItem<'line'>) => `${tooltipItem.dataset.label}: ${tooltipItem.raw}K`,
                },
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    callback: (value: any) => `${value}K`,
                },
            },
        },
    };
    /* **************************************************************************************** */


    /* *********************************** For testimonials *********************************** */
    const [testimonialData, setTestimonialData] = useState<Testimonials[]>([]);

    useEffect(() => {
        setTestimonialData(testimonials);
    }, [testimonialData])

    const swiperRef = useRef<SwiperClass | null>(null);
    const [isAutoplayRunning, setIsAutoplayRunning] = useState<boolean>(true);
    const [selectedReview, setSelectedReview] = useState<Testimonials | null>(null);
    const [showReviewModal, setShowReviewModal] = useState<boolean>(false);

    const handleSlideClick = () => {
        if (swiperRef.current) {
            if (isAutoplayRunning) {
                swiperRef.current.autoplay.stop();
                setIsAutoplayRunning(false);
            } else {
                swiperRef.current.autoplay.start();
                setIsAutoplayRunning(true);
            }
        }
    };

    const handleViewReview = (review: Testimonials) => {
        setSelectedReview(review);
        setShowReviewModal(true);
    };

    const breakpoints = {
        576: {
            spaceBetween: 20,
        },
        768: {
            spaceBetween: 20,
        },
        991: {
            spaceBetween: 15,
        }
    };
    /* **************************************************************************************** */


    return (
        <React.Fragment>
            <InfoTootip target=".testimonial_review" mouseTrack mouseTrackLeft={10} />

            {/* Hero section */}
            <section className="page_initial_section home_hero_section overflow-hidden">
                <Fade triggerOnce >
                    <img src="/assets/images/website/home-page/home_hero_watermark.webp" className="home_hero_wm_img" alt="Traqdo" loading="lazy" draggable="false" />
                    <img src="/assets/images/website/home-page/hero_img_mobile.webp" className="home_hero_wm_img for_mobile" alt="Traqdo" loading="lazy" draggable="false" />
                </Fade>
                <div className="container-md">
                    <div className="home_hero_content">
                        <Fade triggerOnce>
                            <h1 className="home_hero_title text-center">
                                Take the stress out of project planning
                            </h1>
                        </Fade>

                        <Slide direction="up" delay={100} triggerOnce>
                            <p className="home_hero_desc text-center">
                                Effortless Project Planning, Elevated: Traqdo empowers teams to collaborate smarter, faster, and better.
                            </p>
                        </Slide>

                        <Slide direction="up" delay={100} triggerOnce>
                            <div className="home_hero_app_section justify-content-center">
                                <Link target="_blank" to={appStoreLink} className="mb_app_link p-ripple">
                                    <img src="/assets/images/website/home-page/app_store.svg" alt="App Store" loading="lazy" draggable="false" />
                                    <Ripple />
                                </Link>
                                <Link target="_blank" to={playStoreLink} className="mb_app_link p-ripple">
                                    <img src="/assets/images/website/home-page/play_store.svg" alt="Play Store" loading="lazy" draggable="false" />
                                    <Ripple />
                                </Link>
                            </div>
                        </Slide>

                    </div>
                </div>
            </section>

            {/* We do section */}
            <section className="section__padding we_do_section">
                <div className="container-md">
                    <div className="section_content">
                        <Fade delay={100} triggerOnce>
                            <h2 className="section_main_head text-center">
                                We Do Project Management <br /> Without All The Management
                            </h2>
                        </Fade>

                        <Slide direction="up" delay={100} triggerOnce>
                            <p className="section_main_desc text-center">
                                Managing projects shouldn't feel like managing chaos. At Traqdo, we strip away the complexities, delivering a streamlined solution that lets you focus on progress, not processes. With tools designed to simplify scheduling, tracking, and collaboration, we make project management intuitive, efficient, and effective—without the overwhelm.
                            </p>
                        </Slide>
                    </div>
                </div>
            </section>

            {/* Join us section */}
            <JoinUs />

            {/* Why choose section */}
            <section className="section__padding why_choose_section">
                <div className="container-md">
                    <div className="why_choose_content">
                        <Fade delay={100} triggerOnce>
                            <h2 className="section_main_head has_img text-center">
                                Why People Choose&nbsp;
                                <img src="/assets/images/traqdo_logo.svg" alt="Traqdo" className="heading_logo" loading="lazy" draggable="false" />
                            </h2>
                        </Fade>

                        <div className="row why_choose_content_row">
                            <div className="col-12 col-lg-4">
                                <Fade delay={0} triggerOnce>
                                    <Tilt tiltMaxAngleX={8} tiltMaxAngleY={8} className="tile_effect">
                                        <article className="why_choose_container first">
                                            <Fade delay={300} triggerOnce>
                                                <img src="assets/images/website/home-page/why_choose_1.svg" alt="Keep Everything in One Place" loading="lazy" draggable="false" />
                                            </Fade>

                                            <div className="why_choose_container_sub">
                                                <Slide direction="up" delay={0} triggerOnce>
                                                    <h4>Keep Everything <br />in One Place</h4>
                                                </Slide>

                                                <Slide direction="up" delay={50} triggerOnce>
                                                    <p>Traqdo let's you keep all your important tasks in one place, so you can avoid confusion and keep focusing on your important taks.</p>
                                                </Slide>
                                            </div>
                                        </article>
                                    </Tilt>
                                </Fade>
                            </div>
                            <div className="col-12 col-lg-4">
                                <Fade delay={50} triggerOnce>
                                    <Tilt tiltMaxAngleX={8} tiltMaxAngleY={8} className="tile_effect">
                                        <article className="why_choose_container second">
                                            <Fade delay={300} triggerOnce>
                                                <img src="assets/images/website/home-page/why_choose_2.svg" alt="Inexpensive & Fairly priced" loading="lazy" draggable="false" />
                                            </Fade>

                                            <div className="why_choose_container_sub">
                                                <Slide direction="up" delay={0} triggerOnce>
                                                    <h4>Inexpensive & <br />Fairly priced</h4>
                                                </Slide>

                                                <Slide direction="up" delay={50} triggerOnce>
                                                    <p>We priced Traqdo fairly for both, whether you're a solo user or a team you can be sure that you'll find a package that suits your needs.</p>
                                                </Slide>
                                            </div>
                                        </article>
                                    </Tilt>
                                </Fade>
                            </div>
                            <div className="col-12 col-lg-4">
                                <Fade delay={100} triggerOnce>
                                    <Tilt tiltMaxAngleX={8} tiltMaxAngleY={8} className="tile_effect">
                                        <article className="why_choose_container third">
                                            <Fade delay={300} triggerOnce>
                                                <img src="assets/images/website/home-page/why_choose_3.svg" alt="Good for Individual & Entreprises" loading="lazy" draggable="false" />
                                            </Fade>

                                            <div className="why_choose_container_sub">
                                                <Slide direction="up" delay={0} triggerOnce>
                                                    <h4>Good for Individual <br />& Entreprises</h4>
                                                </Slide>

                                                <Slide direction="up" delay={50} triggerOnce>
                                                    <p>Traqdo was designed to be used by both individuals and large enterprises. Our app is the best in the market.</p>
                                                </Slide>
                                            </div>
                                        </article>
                                    </Tilt>
                                </Fade>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features section */}
            <section className="feature_section">
                <article className="feature_container section__padding">
                    <div className="container-md">
                        <div className="feature_content">
                            <Fade delay={100} triggerOnce>
                                <h2 className="section_main_head text-center text-white mb-0">
                                    <img src="/assets/images/traqdo_logo_icon_light.svg" alt="Traqo" className="heading_icon" loading="lazy" draggable="false" />
                                    <br />
                                    Traqdo Features
                                </h2>
                            </Fade>

                            <div className="features_list_area">
                                <div className="row">
                                    <div className="col-12 col-lg-6 pe-lg-5 my-auto">
                                        <div className="feature_content_area">
                                            <Fade triggerOnce>
                                                <img src="/assets/images/website/home-page/track.svg" alt="Track" className="feature_icon" loading="lazy" draggable="false" />
                                            </Fade>

                                            <Slide direction="up" delay={0} triggerOnce>
                                                <h4 className="feature_heading">
                                                    Keep Track of Your <br />
                                                    <span>Most Important Tasks</span>
                                                </h4>
                                            </Slide>

                                            <Slide direction="up" delay={50} triggerOnce>
                                                <p className="feature_desc">
                                                    Stay organized and on top of your priorities with Traqdo's task management system. Easily assign, track, and update tasks to ensure nothing falls through the cracks, helping your team focus on what matters most.
                                                </p>
                                            </Slide>

                                            <div className="overflow-hidden">
                                                <Slide direction="left" triggerOnce>
                                                    <Link to={'#'} className="feature_link" onClick={handleScrollToDownloadApp}>
                                                        Download the App
                                                        <i className="bi bi-arrow-right"></i>
                                                    </Link>
                                                </Slide>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-12 col-lg-6 ">
                                        <div className="feature_concept_img_area d-flex justify-content-center">
                                            <Fade duration={1500} triggerOnce>
                                                <Tilt tiltEnable={false} scale={1.05} transitionSpeed={2500} className="tile_effect">
                                                    <div className="feature_concept_circle is_circle">
                                                        <div className="outer_path_circle is_circle ">
                                                            <div className="outer_path_img_icon first is_circle floating">
                                                                <img src="/assets/images/website/home-page/statics.png" alt="Feature" loading="lazy" draggable="false" />
                                                            </div>

                                                            <div className="outer_path_img_icon second is_circle floating">
                                                                <img src="/assets/images/website/home-page/scale.png" alt="Feature" loading="lazy" draggable="false" />
                                                            </div>

                                                            <div className="outer_path_img_avatar first is_circle floating">
                                                                <img src="/assets/images/website/home-page/avatar_women.webp" alt="Feature" loading="lazy" draggable="false" />
                                                            </div>

                                                            <div className="outer_path_img_avatar second is_circle floating">
                                                                <img src="/assets/images/website/home-page/avatar_men.webp" alt="Feature" loading="lazy" draggable="false" />
                                                            </div>

                                                            <div className="sub_circle is_circle">
                                                                <div className="inner_path_circle is_circle">
                                                                    <div className="inner_path_img_icon is_circle floating">
                                                                        <img src="/assets/images/website/home-page/helmet.png" alt="Feature" loading="lazy" draggable="false" />
                                                                    </div>

                                                                    <div className="inner_path_img_avatar is_circle floating">
                                                                        <img src="/assets/images/website/home-page/avatar_men_1.webp" alt="Feature" loading="lazy" draggable="false" />
                                                                    </div>

                                                                </div>
                                                            </div>
                                                        </div>
                                                        <img src="/assets/images/traqdo_logo_icon.png" alt="Traqo" className="circle_icon_logo center_logo" loading="lazy" draggable="false" />
                                                    </div>
                                                </Tilt>
                                            </Fade>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="features_list_area">
                                <div className="row flex-column-reverse flex-lg-row">
                                    <div className="col-12 col-lg-6">
                                        <Fade duration={1500} triggerOnce>
                                            <div className="feature_concept_img_area d-flex justify-content-center">
                                                <div className="feature_task_slider_area">
                                                    {featureTasks.map((task, index) => (
                                                        <article
                                                            key={task.id}
                                                            className={`feature_task_slider p-ripple ${index === 0 ? 'show' : ''}`}
                                                            style={{ order: index }}
                                                            draggable
                                                            onClick={() => handleClick(task.id)}
                                                            onDragStart={() => handleDragStart(task.id)}
                                                            onDragOver={handleDragOver}
                                                            onDrop={() => handleDrop(task.id)}
                                                        >
                                                            <Ripple />
                                                            <div className="feature_head_area">
                                                                <img src="/assets/images/website/home-page/task_icon.svg" alt="Feature" loading="lazy" draggable="false" />
                                                                <h6>{task.title}</h6>
                                                            </div>
                                                            <div className="feature_body_area">
                                                                <img
                                                                    src="/assets/images/website/home-page/task_graph.svg"
                                                                    className="feature_graph_img"
                                                                    alt="Feature"
                                                                    loading="lazy"
                                                                    draggable="false"
                                                                />
                                                                <img
                                                                    src="/assets/images/website/home-page/task_batch.svg"
                                                                    className="feature_batch_img"
                                                                    alt="Feature"
                                                                    loading="lazy"
                                                                    draggable="false"
                                                                />
                                                            </div>
                                                        </article>
                                                    ))}
                                                </div>
                                            </div>
                                        </Fade>
                                    </div>

                                    <div className="col-12 col-lg-6 pe-lg-5 my-auto">
                                        <div className="feature_content_area">
                                            <Fade triggerOnce>
                                                <img src="/assets/images/website/home-page/time_line.svg" alt="Task Deadline" className="feature_icon" loading="lazy" draggable="false" />
                                            </Fade>

                                            <Slide direction="up" delay={0} triggerOnce>
                                                <h4 className="feature_heading">
                                                    Set tasks with <br />
                                                    <span>Realistic Deadlines</span>
                                                </h4>
                                            </Slide>

                                            <Slide direction="up" delay={50} triggerOnce>
                                                <p className="feature_desc">
                                                    Achieve project success by setting achievable and realistic deadlines for every task. With Traqdo’s intuitive scheduling tools, you can confidently map out timelines, ensuring your team stays on track and delivers on time, every time.
                                                </p>
                                            </Slide>

                                            <div className="overflow-hidden">
                                                <Slide direction="left" triggerOnce>
                                                    <Link to={'/auth/login'} className="feature_link" onClick={goToTop}>
                                                        Get Started Now
                                                        <i className="bi bi-arrow-right"></i>
                                                    </Link>
                                                </Slide>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="features_list_area">
                                <div className="row">
                                    <div className="col-12 col-lg-6 pe-lg-5 my-auto">
                                        <div className="feature_content_area">
                                            <Fade triggerOnce>
                                                <img src="/assets/images/website/home-page/money.svg" alt="Expence" className="feature_icon" loading="lazy" draggable="false" />
                                            </Fade>

                                            <Slide direction="up" delay={0} triggerOnce>
                                                <h4 className="feature_heading">
                                                    Keep Track of <br />
                                                    <span>Your Expenses</span>
                                                </h4>
                                            </Slide>

                                            <Slide direction="up" delay={50} triggerOnce>
                                                <p className="feature_desc">
                                                    Maintain full control over your project budget with Traqdo’s expense tracking feature. Monitor costs in real-time, track spending against your budget, and make informed decisions to keep your project financially on track throughout its lifecycle.
                                                </p>
                                            </Slide>

                                            <div className="overflow-hidden">
                                                <Slide direction="left" triggerOnce>
                                                    <Link to={'/pricing'} className="feature_link" onClick={goToTop}>
                                                        Plans & Pricing
                                                        <i className="bi bi-arrow-right"></i>
                                                    </Link>
                                                </Slide>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-12 col-lg-6 d-flex justify-content-center">
                                        <Fade duration={1500} triggerOnce className="feature_graph_area">
                                            <article className="feature_graph_container">
                                                <div className="feature_graph_header">
                                                    <span className="header_circle"></span>
                                                    <span className="header_circle"></span>
                                                    <span className="header_circle"></span>
                                                </div>
                                                <div className="feature_graph_body">
                                                    <Line data={chartData} options={options} />
                                                </div>
                                            </article>
                                        </Fade>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </article>
            </section>

            <div className="section__padding bg-white pt-0"></div>

            {/* Testimonial section */}
            {/* <section className="testimonial_section">
                <div className="section__padding">
                    <article className="testimonial_container">
                        <button type="button" className="testimonial_slider_nav_btn prev_tab_btn p-ripple">
                            <i className="bi bi-chevron-left"></i>
                            <Ripple />
                        </button>
                        <button type="button" className="testimonial_slider_nav_btn next_tab_btn p-ripple">
                            <i className="bi bi-chevron-right"></i>
                            <Ripple />
                        </button>
                        <Fade triggerOnce>
                            <img src="/assets/images/website/home-page/testimonial_person.webp" className="testimonial_person_img" alt="Testimonial" loading="lazy" draggable="false" />
                        </Fade>
                        <div className="container-md">
                            <div className="row">
                                <div className="col-12 offset-xl-6 col-xl-6 col-lg-7 offset-lg-5">
                                    <Fade delay={50} triggerOnce>
                                        {testimonialData && testimonialData?.length > 0 && (
                                            <Swiper
                                                className="testimonial_slider_area"
                                                modules={[Navigation, Autoplay]}
                                                spaceBetween={10}
                                                slidesPerView={1}
                                                breakpoints={breakpoints}
                                                loop={true}
                                                navigation={{
                                                    nextEl: '.next_tab_btn',
                                                    prevEl: '.prev_tab_btn',
                                                }}
                                                autoplay={{
                                                    delay: 5000,
                                                    disableOnInteraction: false,
                                                }}
                                                speed={1500}
                                                grabCursor={true}
                                                onSwiper={(swiper: SwiperClass) => {
                                                    swiperRef.current = swiper;
                                                }}
                                            >
                                                {testimonialData?.map((testimonial) => (
                                                    <SwiperSlide
                                                        key={testimonial.id}
                                                        onClick={handleSlideClick}>
                                                        <article className="testimonial_slider p-ripple">
                                                            <img src="/assets/images/website/home-page/quotation-mark.svg" className="quotation_mark" alt="Quote" loading="lazy" draggable="false" />
                                                            <h4 className="testimonial_head">
                                                                Testimonials
                                                            </h4>
                                                            <p
                                                                className="testimonial_review"
                                                                data-pr-tooltip="Click to view the full review"
                                                                data-pr-position="top"
                                                                data-pr-classname="info_tooltip"
                                                                onClick={() => handleViewReview(testimonial)}
                                                            >
                                                                "{testimonial.review.length > 250 ? `${testimonial.review.substring(0, 250)}...` : testimonial.review}"
                                                            </p>

                                                            <div className="testimonial_slider_footer">
                                                                <img src={`${testimonial.profile_image ? testimonial.profile_image : '/assets/images/website/home-page/testimonial-avatar.png'}`} alt="Profile" loading="lazy" draggable="false" />
                                                                <div className="testimonial_profile_info">
                                                                    <h5>{testimonial.profile_name ? testimonial.profile_name : 'Unknown'}</h5>
                                                                    <p>{testimonial.role + ', ' + testimonial?.working_place}</p>
                                                                </div>
                                                            </div>
                                                            <Ripple />
                                                        </article>
                                                    </SwiperSlide>
                                                ))}
                                            </Swiper>
                                        )}
                                    </Fade>
                                </div>
                            </div>
                        </div>
                    </article>
                </div>
            </section> */}

            {/* Download app section */}
            <DownloadApp downloadAppRef={downloadAppRef} />

            {/* Review modal */}
            <Dialog
                headerClassName="d-none"
                dismissableMask
                visible={showReviewModal}
                className="custom_modal_dialog modal_dialog_md"
                onHide={() => { if (!showReviewModal) return; setShowReviewModal(false); }}
            >
                <div className="custom_modal_body">
                    <button className="custom_modal_close position-absolute float p-ripple"
                        onClick={() => setShowReviewModal(false)}>
                        <i className="bi bi-x-lg"></i>
                        <Ripple />
                    </button>
                    <h6 className="review_head">{selectedReview?.profile_name} <span>says about Trqdo.</span></h6>
                    <p className="review_paragraph">
                        {selectedReview?.review}
                    </p>
                </div>
            </Dialog>
        </React.Fragment>
    )
}

export default Home;